// Panel layout — the rail + dock widths, persisted per device (pre-sync).
//
// This is DISPLAY CHROME, not the working set: like the theme (theme.tsx) and
// the display name (displayName.ts), the author's chosen rail/dock widths
// round-trip through their OWN localStorage key and are never a domain event or
// part of the `viewState` working-set blob (which persists open tabs — U46 —
// deliberately NOT the chrome; see AppShellHost's rail-sections comment). Only
// the impure shell reads/writes; the parse/serialize + clamps are pure and
// unit-tested.
//
// Parse, don't validate: localStorage hands back a string written by ANY earlier
// build. Both widths are re-established here and CLAMPED at the boundary, so a
// corrupt/out-of-range value degrades to a sane width rather than a broken
// layout. A missing value is the default.

import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

const STORAGE_KEY = "bilda.panelLayout";

// The left rail (expanded) and the right dock (open) widths, in CSS px. Clamps
// keep both usable: never so narrow the labels clip, never so wide the editor
// starves. Exported so the resize handle's aria-value range + the host's clamp
// read one source of truth.
export const RAIL = { def: 232, min: 180, max: 420 } as const;
// The dock's default holds the 380 px chat panel (pen "Chattpanel") plus its
// 16 px gutter on the right (ChromeShellFrame.css).
export const DOCK = { def: 396, min: 280, max: 560 } as const;

// Drag-to-expand tuning (drag-to-open the collapsed dock / rail — panelDragOpen.
// ts). SHORTCUT: `RAIL_COLLAPSED_PX` mirrors the icon-rail's collapsed width
// (`--sidebar-rail-width` = 52px in tokens.css) — kept here so the pure drag math and
// the CSS share one number; if that token changes, change this too.
// `OPEN_PULL_THRESHOLD_PX` is how far past the collapsed edge a pull must travel
// before release commits to open (short of it, it snaps back).
export const RAIL_COLLAPSED_PX = 52;
export const OPEN_PULL_THRESHOLD_PX = 96;

export type PanelLayout = {
    readonly railWidth: number;
    readonly dockWidth: number;
};

export const defaultPanelLayout: PanelLayout = {
    railWidth: RAIL.def,
    dockWidth: DOCK.def,
};

const clamp = (n: number, lo: number, hi: number): number =>
    Math.min(hi, Math.max(lo, n));

// A finite number within [min, max], else the default — the one place a width
// is sanitised (both the parse boundary and every live resize route through it).
export const clampRail = (n: number): number =>
    Number.isFinite(n) ? clamp(Math.round(n), RAIL.min, RAIL.max) : RAIL.def;
export const clampDock = (n: number): number =>
    Number.isFinite(n) ? clamp(Math.round(n), DOCK.min, DOCK.max) : DOCK.def;

// --- Parsing / serializing (pure) --------------------------------------------

export const parsePanelLayout = (raw: string | null): PanelLayout => {
    if (raw === null) return defaultPanelLayout;
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        return defaultPanelLayout;
    }
    if (typeof data !== "object" || data === null) return defaultPanelLayout;
    const rail =
        "railWidth" in data && typeof data.railWidth === "number"
            ? clampRail(data.railWidth)
            : RAIL.def;
    const dock =
        "dockWidth" in data && typeof data.dockWidth === "number"
            ? clampDock(data.dockWidth)
            : DOCK.def;
    return { railWidth: rail, dockWidth: dock };
};

export const serializePanelLayout = (layout: PanelLayout): string =>
    JSON.stringify({
        railWidth: clampRail(layout.railWidth),
        dockWidth: clampDock(layout.dockWidth),
    });

// --- The impure seam (host-only, like displayName.ts) ------------------------

export const readPanelLayout = (): PanelLayout =>
    parsePanelLayout(readDeviceKey(STORAGE_KEY));

export const writePanelLayout = (layout: PanelLayout): void =>
    writeDeviceKey(STORAGE_KEY, serializePanelLayout(layout));
